import AsyncStorage from '@react-native-async-storage/async-storage';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { InboxItem, ObjectType } from '../generated/gql-types';
import { inboxItemActions } from './inboxItem.action';
import { upsertOne } from '../util/zustand.util';

const isUnreadjobTimesUpdateInboxItemForJob = (
  item: InboxItem,
  jobId: string,
): boolean =>
  item.relevantObjectType === ObjectType.Job &&
  item.relevantObjectId === jobId &&
  !item.readAt;

interface InboxItemsState {
  inboxItems: InboxItem[];
  fetchInboxItemsForOwner: (ownerId: string) => Promise<void>;
  markAsRead: (inboxItemId: string) => Promise<void>;
  hasUnreadJobTimesUpdate: (jobId: string) => boolean;
  getUnreadjobTimesUpdateInboxItemIdForJob: (
    jobId: string,
  ) => string | undefined;
  getUnreadjobTimesUpdateInboxItemForJob: (
    jobId: string,
  ) => InboxItem | undefined;
}

export const useInboxItemsStore = create<InboxItemsState>()(
  persist(
    (set, get) => ({
      inboxItems: [],

      fetchInboxItemsForOwner: async (ownerId: string) => {
        const inboxItems =
          await inboxItemActions.getUnreadInboxItemsForOwner(ownerId);
        set({ inboxItems });
      },

      markAsRead: async (inboxItemId: string) => {
        const priorItems = get().inboxItems;
        const targetItem = priorItems.find((item) => item.id === inboxItemId);
        if (!targetItem || targetItem.readAt) {
          return;
        }

        const readAt = new Date().valueOf().toString();
        set({
          inboxItems: priorItems.map((item) =>
            item.id === inboxItemId ? { ...item, readAt } : item,
          ),
        });

        try {
          await inboxItemActions.markInboxItemAsRead(inboxItemId);
          const updatedItem = await inboxItemActions.getInboxItem(inboxItemId);
          set({
            inboxItems: upsertOne(priorItems, updatedItem),
          });
        } catch (error) {
          set({ inboxItems: priorItems });
          throw error;
        }
      },

      hasUnreadJobTimesUpdate: (jobId: string) =>
        get().inboxItems.some((item) =>
          isUnreadjobTimesUpdateInboxItemForJob(item, jobId),
        ),

      getUnreadjobTimesUpdateInboxItemIdForJob: (jobId: string) =>
        get().inboxItems.find((item) =>
          isUnreadjobTimesUpdateInboxItemForJob(item, jobId),
        )?.id,

      getUnreadjobTimesUpdateInboxItemForJob: (jobId: string) =>
        get().inboxItems.find((item) =>
          isUnreadjobTimesUpdateInboxItemForJob(item, jobId),
        ),
    }),
    {
      name: 'inbox-items-storage',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({
        inboxItems: state.inboxItems,
      }),
    },
  ),
);
